import { BabyIcon as Baby } from "@phosphor-icons/react/dist/csr/Baby";
import { BriefcaseIcon as Briefcase } from "@phosphor-icons/react/dist/csr/Briefcase";
import { ChalkboardIcon as Chalkboard } from "@phosphor-icons/react/dist/csr/Chalkboard";
import { CodeIcon as Code } from "@phosphor-icons/react/dist/csr/Code";
import { GiftIcon as Gift } from "@phosphor-icons/react/dist/csr/Gift";
import { GraduationCapIcon as GraduationCap } from "@phosphor-icons/react/dist/csr/GraduationCap";
import { HardHatIcon as HardHat } from "@phosphor-icons/react/dist/csr/HardHat";
import { HeartIcon as Heart } from "@phosphor-icons/react/dist/csr/Heart";
import { HouseIcon as House } from "@phosphor-icons/react/dist/csr/House";
import { PaletteIcon as Palette } from "@phosphor-icons/react/dist/csr/Palette";
import { StethoscopeIcon as Stethoscope } from "@phosphor-icons/react/dist/csr/Stethoscope";
import { UsersThreeIcon as UsersThree } from "@phosphor-icons/react/dist/csr/UsersThree";
import { XIcon as X } from "@phosphor-icons/react/dist/csr/X";
import { useEffect, useId, useRef } from "react";
import type { ShopperAudience, ShopperAudienceIcon } from "./api.js";
import type { messages } from "./messages.js";

const STORAGE_KEY = "zoya.storefront.audience";
/** Stored when the visitor chose to browse everything instead of an audience. */
export const NO_AUDIENCE = "__everyone";

export const readStoredAudience = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};
export const storeAudience = (slug: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, slug);
  } catch {
    // Private mode: the choice simply lasts for this page view.
  }
};

const icons: Record<ShopperAudienceIcon, typeof UsersThree> = {
  student: GraduationCap,
  doctor: Stethoscope,
  engineer: HardHat,
  teacher: Chalkboard,
  parent: Baby,
  artist: Palette,
  professional: Briefcase,
  developer: Code,
  gift: Gift,
  home: House,
  couple: Heart,
  general: UsersThree,
};

export function AudienceIcon({
  icon,
  className,
}: {
  icon: ShopperAudienceIcon;
  className?: string;
}) {
  const Icon = icons[icon] ?? UsersThree;
  return <Icon className={className} aria-hidden="true" weight="duotone" />;
}

/**
 * Modal shown on the home page until the visitor picks who they are shopping
 * as. Native <dialog>: focus is trapped and Escape closes it, which counts as
 * "show me everything" so the question is not asked again.
 */
export function AudiencePicker({
  audiences,
  selected,
  onChoose,
  onDismiss,
  t,
}: {
  audiences: ShopperAudience[];
  selected: string | null;
  onChoose: (slug: string) => void;
  onDismiss: () => void;
  t: (key: keyof typeof messages.en) => string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  return (
    <dialog
      ref={ref}
      className="audience-picker"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onDismiss();
      }}
    >
      <div className="audience-picker__panel">
        <button
          type="button"
          className="audience-picker__close"
          aria-label={t("audience.close")}
          onClick={onDismiss}
        >
          <X aria-hidden="true" />
        </button>
        <p className="eyebrow">{t("audience.eyebrow")}</p>
        <h2 id={titleId}>{t("audience.title")}</h2>
        <p id={bodyId} className="audience-picker__body">
          {t("audience.body")}
        </p>
        <ul className="audience-picker__grid">
          {audiences.map((audience) => (
            <li key={audience.publicId}>
              <button
                type="button"
                className="audience-option"
                aria-pressed={selected === audience.slug}
                onClick={() => onChoose(audience.slug)}
              >
                <span className="audience-option__icon">
                  <AudienceIcon icon={audience.icon} />
                </span>
                <span className="audience-option__text">
                  <strong>{audience.name}</strong>
                  {audience.tagline ? <span>{audience.tagline}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="audience-picker__skip"
          onClick={onDismiss}
        >
          {t("audience.skip")}
        </button>
      </div>
    </dialog>
  );
}
